import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import { FieldValues, PasswordElement, Path, TextFieldElement, TextFieldElementProps, useWatch } from 'react-hook-form-mui'
import { useFormContext } from 'react-hook-form'

interface IProps<T extends FieldValues> extends TextFieldElementProps {
    name: Path<T>
    readOnly?: boolean
    startAdornment?: React.ReactNode
    min?: number
    max?: number
    endAdornment?: React.ReactNode
    readOnlyFormat?: (value: string | number | null | undefined) => string
    fixedDecimalScale?: number
    integerOnly?: boolean
}

export default function ValidatedTextField<T extends FieldValues>(props: IProps<T>) {
    const { setValue } = useFormContext<T>()

    const toFormValue = (value: string | number | null | undefined) => {
        // react-hook-form-mui represents an empty numeric input as null.
        // Do not pass it to Number(), because Number(null) is 0.
        if (value == null || value === '') return null
        if (props.type !== 'number') return value

        const numberValue = typeof value === 'number' ? value : Number(value)
        return Number.isFinite(numberValue) ? numberValue : value
    }

    if (!props.name) {
        throw new Error("ValidatedTextField requires a non-empty name");
    }

    const fieldValue = useWatch({ name: props.name }) as unknown as string | number | null | undefined

    const sharedProps = {
        name: props.name,
        fullWidth: true,
        size: 'small' as const,
        required: props.required,
        rules: {
            setValueAs: toFormValue,
            validate: (v: string | number) => {
                const str = v == null ? '' : String(v).trim()
                if (props.required && !str) return 'Field cannot be empty'
                if ((props.type === 'number' || props.fixedDecimalScale !== undefined) && str) {
                    const n = typeof v === 'string' ? Number(str) : (v as number)
                    if (!Number.isFinite(n)) return 'Enter a valid number'
                    if (props.integerOnly && !Number.isInteger(n)) return 'Enter a whole number'
                    if (props.min !== undefined && n < props.min) return `Value must be at least ${props.min}`
                    if (props.max !== undefined && n > props.max) return `Value must be at most ${props.max}`
                }
                return true
            },
        },
        disabled: props.disabled ?? false,
        autoComplete: 'off' as const,
        inputProps: { readOnly: props.readOnly ?? false, ...props.inputProps },
        InputProps: {
            startAdornment: props.startAdornment ? <InputAdornment position="start">{props.startAdornment}</InputAdornment> : undefined,
            endAdornment: props.endAdornment ? <InputAdornment position="end">{props.endAdornment}</InputAdornment> : undefined,
            ...props.InputProps,
        },
        multiline: props.multiline ?? false,
        label: props.label,
        variant: props.variant,
    }

    if (props.type === 'password') return <PasswordElement {...sharedProps} />

    const formatted = props.readOnly && props.readOnlyFormat
        ? (props.readOnlyFormat(fieldValue) ?? '')
        : (fieldValue ?? '')

    if (props.readOnly && props.readOnlyFormat) {
        return (
            <TextField
                fullWidth
                size='small'
                label={props.label}
                value={formatted}
                multiline={props.multiline ?? false}
                rows={props.rows}
                variant={props.variant}
                inputProps={{ readOnly: true, ...props.inputProps }}
                InputProps={{
                    startAdornment: props.startAdornment ? <InputAdornment position="start">{props.startAdornment}</InputAdornment> : undefined,
                    endAdornment: props.endAdornment ? <InputAdornment position="end">{props.endAdornment}</InputAdornment> : undefined,
                    ...props.InputProps,
                }}
            />
        )
    }

    return (
        <TextFieldElement
            {...sharedProps}
            type={props.type}
            value={formatted}
            rows={props.rows}
            onChange={(e) => {
                const v = e.target.value
                if (v === '') {
                    setValue(props.name, null as any, { shouldDirty: true, shouldValidate: true })
                    return
                }
                // pass through original onChange if provided
                props.onChange?.(e)
                // HTML inputs emit strings, but API number properties must be sent as JSON numbers.
                setValue(props.name, toFormValue(v) as any, { shouldDirty: true })
            }}
            onBlur={(e) => {
                if (props.fixedDecimalScale !== undefined) {
                    const raw = e.target.value
                    if (raw !== '') {
                        const parsed = Number(raw)
                        if (!Number.isNaN(parsed)) {
                            const fixed = Number(parsed.toFixed(props.fixedDecimalScale))
                            setValue(props.name, fixed as any, { shouldDirty: true, shouldValidate: true })
                        }
                    }
                }
                props.onBlur?.(e)
            }}
        />
    )
}
